import { useEffect, useRef, useState } from "react";

//import components
import Navbar from "../components/SpectraNavbar";
import Footer from "../components/Footer";
import Marker from "../components/map/Marker";
import Popup from "../components/map/Popup";
import Filter from "../components/map/Filter";
import StyleSelections from "../components/map/StyleSelections";

import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

import Spinner from "react-bootstrap/Spinner";

import { useUserContext, useAdminContext } from "../contexts";

import { useMapCustomerSamples } from "../api/map";

export default function Map() {
  const { user, settings } = useUserContext();
  const { selectedCustomer, customers } = useAdminContext();
  const userLocations = user.locID;
  const userRole = user.role;

  const [styles, setStyles] = useState({
    mapStyle: "mapbox://styles/mapbox/dark-v10",
    markerShape: "bi bi-geo-alt-fill",
    markerColourNormal: "#1CA3F0",
    markerColourActive: "#FFFFFF",
    markerSize: 40,
  });
  const [mapStyleLoaded, setMapStyleLoaded] = useState(false);
  const [currentLocation, setCurrentLocation] = useState([-76.493, 44.2334]); // Default location Kingston, ON
  const [sampleData, setSampleData] = useState({
    type: "FeatureCollection",
    features: [],
  });
  const [filteredData, setFilteredData] = useState({
    type: "FeatureCollection",
    features: [],
  });
  const [activeFeature, setActiveFeature] = useState();
  const [activeCustomer, setActiveCustomer] = useState(null);

  const today = new Date();
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(today.getDate() - 7);
  const defaultFilterState = {
    location: [],
    substance: [],
    startDate: sevenDaysAgo.toISOString().split("T")[0], // Format to YYYY-MM-DD
    endDate: today.toISOString().split("T")[0], // Format to YYYY-MM-DD
    texture: [],
    colour: [],
  };

  const [filter, setFilter] = useState(defaultFilterState);

  const mapRef = useRef();
  const mapContainerRef = useRef();

  const { data: mapSamples, isLoading: isSamplesLoading } =
    useMapCustomerSamples(
      filter.startDate,
      filter.endDate, // Set to the end of day
      userRole !== "admin" ? userLocations : null, // Use user locations if available
      selectedCustomer
    );

  //set current location
  useEffect(() => {
    if (!userLocations || userLocations.length === 0) return;
    if (!settings || !settings.currentLoc) return;
    const location = userLocations.find(
      (loc) => loc.locationId === settings.currentLoc
    );

    setCurrentLocation([location.long, location.lat]); // Set the current location based on user settings
    if (mapRef.current) {
      mapRef.current.flyTo({
        center: [location.long, location.lat],
        zoom: 11,
      });
    }
  }, [userLocations, settings]);

  //set sample data
  useEffect(() => {
    if (!isSamplesLoading && mapSamples) {
      setSampleData({
        type: "FeatureCollection",
        features: mapSamples.samples
          .filter((sample) => sample.geometry)
          // Filter out samples without geometry
          .map((sample) => ({
            type: "Feature",
            geometry: sample.geometry,
            properties: {
              _id: sample._id,
              customerId: sample.customerId,
              locationId: sample.locationId,
              sampleId: sample.sampleId,
              processedDate: sample.processedDate,
              findings: sample.tests.flatMap((test) =>
                test.rb_findings
                  ? test.rb_findings.map((finding) => finding.substance)
                  : test.findings?.map((finding) => finding.substance) || []
              ),
              colour: sample.colourId,
              texture: sample.texture,
            },
          })),
      });
    }
  }, [isSamplesLoading, mapSamples]);

  useEffect(() => {
    setFilteredData({
      type: "FeatureCollection",
      features: sampleData.features?.filter((feature) => {
        if (
          feature.geometry.coordinates[0] > 180 ||
          feature.geometry.coordinates[0] < -180 ||
          feature.geometry.coordinates[1] > 90 ||
          feature.geometry.coordinates[1] < -90
        ) {
          return false;
        }
        // Apply filters based on the filter state
        const matchesLocation =
          filter.location.length === 0 ||
          filter.location.includes(feature.properties.locationId);
        const matchesSubstance =
          filter.substance.length === 0 ||
          filter.substance.some((substance) => {
            return feature.properties.findings.includes(substance);
          });
        const matchesColour =
          filter.colour.length === 0 ||
          filter.colour.some((colour) => {
            return feature.properties.colour?.includes(colour);
          });
        const featureTextureId =
          feature?.properties?.texture?.id ?? feature?.properties?.texture;
        const matchesTexture =
          filter.texture.length === 0 ||
          filter.texture.includes(featureTextureId);

        return (
          matchesLocation && matchesSubstance && matchesColour && matchesTexture
        );
      }),
    });
  }, [filter, sampleData]);

  // Initialize Mapbox map when style changes; we intentionally do not include
  // `userRole` or `currentLocation` to avoid re-creating the map on every
  // user change. eslint exhaustive-deps would suggest adding them, but this is
  // a deliberate choice because we manage map viewport elsewhere.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

    if (!mapboxgl.accessToken) {
      console.error(
        "Mapbox access token is not set. Please check your .env file."
      );
      return;
    }

    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      center: userRole === "admin" ? [-95, 51] : currentLocation, //starting position [lng, lat]
      style: styles.mapStyle, //map style
      zoom: userRole === "admin" ? 3 : 10,
    });

    mapRef.current.addControl(new mapboxgl.NavigationControl());
    mapRef.current.addControl(new mapboxgl.FullscreenControl());

    // Add geolocate control to the map.
    mapRef.current.addControl(
      new mapboxgl.GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true,
        },
        trackUserLocation: true,
        showUserHeading: true,
      })
    );

    // Wait for style to load before rendering markers
    mapRef.current.on("style.load", () => {
      // You may want to trigger a state update here to force re-render of markers
      setMapStyleLoaded(true); // Add a state variable for this
    });

    return () => {
      mapRef.current.remove();
      setMapStyleLoaded(false);
    };
  }, [styles.mapStyle]);

  const handleMarkerClick = (feature) => {
    setActiveFeature(feature);
    setActiveCustomer(
      customers.find(
        (customer) => customer.customerId === feature.properties.customerId
      )
    );
  };

  return (
    <div className='flex-container'>
      <Navbar signedIn={!!user} />

      <div
        className='px-sm-3 px-lg-5 py-3 flex-body full-height'
        style={{ marginTop: "79px" }}
      >
        <div className='shadow card full-height'>
          <div
            className='card-body full-height d-flex flex-column justify-content-around'
            style={{ position: "relative" }}
          >
            <div style={{ position: "absolute", zIndex: 1, top: 25, left: 25 }}>
              <Filter
                setFilter={setFilter}
                filter={filter}
                sampleData={sampleData}
                isSamplesLoading={isSamplesLoading}
                userLocations={userLocations}
                setActiveFeature={setActiveFeature}
                defaultFilterState={defaultFilterState}
                userRole={userRole}
              />
            </div>
            <div
              style={{ position: "absolute", zIndex: 1, top: 25, left: 110 }}
            >
              <StyleSelections setStyles={setStyles} styles={styles} />
            </div>
            {isSamplesLoading && (
              <div
                className='text-center bg-light border border-3 p-3 h4'
                style={{
                  position: "absolute",
                  zIndex: 1,
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              >
                <Spinner
                  animation='border'
                  role='status'
                  className='mx-2'
                ></Spinner>
                <span>Loading Samples...</span>
              </div>
            )}
            <div
              id='map-container'
              className='w-100 full-height'
              style={{ background: "lightGrey", zIndex: 0 }}
              ref={mapContainerRef}
            />

            {mapRef.current &&
              mapStyleLoaded &&
              filteredData &&
              filteredData.features.map((feature) => {
                return (
                  <Marker
                    key={feature.properties.sampleId}
                    map={mapRef.current}
                    feature={feature}
                    isActive={
                      activeFeature?.properties.sampleId ===
                      feature.properties.sampleId
                    }
                    onClick={handleMarkerClick}
                    styles={styles}
                  />
                );
              })}
            {mapRef.current && (
              <Popup
                map={mapRef.current}
                activeFeature={activeFeature}
                setActiveFeature={setActiveFeature}
                userLocations={userLocations}
                activeCustomer={activeCustomer}
                setActiveCustomer={setActiveCustomer}
                userRole={userRole}
              />
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
